import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import { RecordsOverviewSandbox } from '@common/sandboxes/overview/records-overview.sandbox.abstract.service';

import { Injectable } from '@angular/core';
import { RecordsService } from '@mediahaven/shared';
import {
    Combine,
    FragmentId,
    RecordId,
    RecordPhase,
    RecordRestrictions,
    Restriction,
    sessionStorageHelper,
    ZoneRestrictions,
    ZonesService
} from '@stylehaven/components';

@Injectable({ providedIn: 'root' })
export class RecordChildrenSandbox extends RecordsOverviewSandbox {
    constructor(
        private readonly recordsService: RecordsService,
        private readonly zoneService: ZonesService,
        private readonly zoneRestrictions: ZoneRestrictions
    ) {
        super();
    }

    public getRestrictions(parentId: RecordId, parentFragmentId: FragmentId): Observable<Restriction> {
        return combineLatest([this.zoneService.zones$, this.getGeolocationRestriction()]).pipe(
            map(([zones, geolocationRestriction]) => {
                const statusRestrictions: Restriction[] = [
                    RecordRestrictions.phaseRestriction([RecordPhase.Published, RecordPhase.Archived])
                ];

                if (sessionStorageHelper.has('shareToken')) {
                    statusRestrictions.push(RecordRestrictions.conceptRestrictionShare());
                }
                // Only add ConceptRestriction if user has zones
                else if (zones && zones.length > 0) {
                    statusRestrictions.push(RecordRestrictions.conceptZoneRestriction(zones));
                }

                const parentRestriction: Restriction = Combine.Or(
                    RecordRestrictions.fragmentOfMainFragmentRestriction(parentFragmentId),
                    Combine.And(
                        Combine.Or(
                            RecordRestrictions.candidateChildOfParentRestriction(parentFragmentId),
                            RecordRestrictions.childOfParentRestriction(parentId)
                        ),
                        RecordRestrictions.notPureFragment()
                    )
                );

                return Combine.And(
                    this.zoneRestrictions.regularRecordTypesZone(),
                    geolocationRestriction,
                    parentRestriction,
                    Combine.Or(...statusRestrictions)
                );
            }),
            shareReplay(1)
        );
    }

    public hasChildren(parentId: RecordId, parentFragmentId: FragmentId): Observable<boolean> {
        return this.getRestrictions(parentId, parentFragmentId).pipe(
            switchMap(restrictions => this.recordsService.countRecords([], restrictions)),
            map(count => count > 0)
        );
    }
}
